// ============================================================================
// Escalation ruling — whose failure a missed cutoff actually is.
//
// This is a TRANSPORTER'S dashboard. A shipment can miss its SLA because the
// truck ran late, or because the material was never ready, the address was wrong,
// or the customer asked to move the slot — and only the first kind is the
// vendor's. So an escalation raised on a shipment either OWNS the delay (the
// breach stands, and on a pickup it stands whatever the clock said) or EXCUSES it
// (the shipment drops out of every breach count on the page).
//
// The gate is the "escalation raised" TAG, not merely a non-null escalation: the
// escalation must have been raised on the trip's own IST dispatch day (see
// `raisedOnTripDate`). An escalation carried over from an attempt weeks ago
// explains nothing about today's run, and letting it rule on today's verdict would
// quietly forgive — or re-punish — every repeat offender.
//
// RESOLVING CHANGES NOTHING HERE. What happened on the day is what it is: a
// transporter's SLA must not move because the desk closed a ticket that evening,
// and a verdict that flips hours later is one nobody can reconcile. So every read
// goes through `escalationOf`, which prefers the row's full day record over the
// still-open-only field.
//
// THE RULES (ops), by issue type:
//
//   THE TRANSPORTER'S, always:
//     Vehicle Delayed · Vehicle Not Found · Unloaders Not Available ·
//     Missed by Delivery Team
//
//   NOTICE-DEPENDENT (last mile only) — raised LATE is an excuse made after the
//   fact, so the breach stands; raised EARLY is genuine notice, so it does not:
//     Site Closing Time    from 5 PM standard · 1 PM express · order +5h same-day express
//     Reschedule Request   from 6 PM standard · 2 PM express · order +5h same-day express
//
//   EVERYTHING ELSE — excused. Material Unavailable, Vendor Payment Issue,
//   Data/PO Error, Incorrect Address, Cancellation Request: someone else's cause.
//
// SAME-DAY EXPRESS runs on a rolling 5-hour clock rather than a fixed hour, so the
// notice window is that clock — the order's own cutoff. A fixed 1 PM or 2 PM
// applied to an order placed at 4 PM sits BEFORE the order existed, which would
// make notice impossible to give and every such escalation a breach by arithmetic.
//
// FIRST MILE (pickups) is decided by the issue type alone, and the escalation
// OUTRANKS the 8 PM cutoff in both directions: one of the four vendor-fault types
// makes the pickup a breach however early it ran, and anything else excuses it.
// The 8 PM rule still decides every pickup nobody escalated. A pickup carries no
// delivery-hour promise for the notice rules to measure against, which is why the
// two notice types simply excuse there and the express flag plays no part.
//
// An issue type we do not recognise excuses, deliberately: the fault list is the
// closed one, and a new picker option should not start charging vendors for
// breaches before ops has decided it is theirs.
// ============================================================================
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { BookingEscalation, LogisticsOrderRow } from "../types";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST = "Asia/Kolkata";


// The escalation this page judges on: the day's record if the backend sends one, else the open
// escalation. The fallback is what every row looked like before `escalation_raised` existed, so a
// payload without it behaves exactly as this dashboard always has.
export const escalationOf = (r: LogisticsOrderRow): BookingEscalation | null =>
  r.escalation_raised ?? r.escalation ?? null;

// Was this escalation raised on the same IST day the truck was due to dispatch? False whenever
// either date is missing — with nothing to compare, we cannot claim it was. This is the condition
// behind the "escalation raised" tag, and the gate on every ruling below: the tag being visible on
// a row is exactly what makes its escalation eligible to rule on that row's verdict.
export const raisedOnTripDate = (r: LogisticsOrderRow): boolean => {
  const raisedAt = escalationOf(r)?.raised_at;
  if (!raisedAt || !r.target_dispatch_start) return false;
  const raised = dayjs.utc(raisedAt);
  const dispatch = dayjs.utc(r.target_dispatch_start);
  if (!raised.isValid() || !dispatch.isValid()) return false;
  return raised.tz(IST).isSame(dispatch.tz(IST), "day");
};

// The ruling itself (which escalations forgive a breach) now lives server-side in
// oms/logistics/services/breach.py, with its tests in oms/tests/unit/test_breach_rule.py.
// What stays here is the day-gate, which the row's 'escalation raised' tag reads.
